/**
 * Mehrsprachigkeit.
 *
 * Standardsprache ist Deutsch. Die Struktur ist bewusst schlank gehalten und
 * ohne zusaetzliche Abhaengigkeit umgesetzt; die Sprachauswahl wird bereits
 * persistiert und die Zahlen-/Datumsformatierung ist an das Gebietsschema
 * gekoppelt. Eine weitere Sprache erfordert lediglich eine neue
 * Woerterbuchdatei und einen Eintrag in `LOCALES`.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { de, type TranslationDictionary, type TranslationKeys } from './de.js';
import { en } from './en.js';

export type LocaleCode = 'de' | 'en';

export const LOCALES: Record<LocaleCode, { label: string; dictionary: TranslationDictionary; intlLocale: string }> = {
  de: { label: 'Deutsch', dictionary: de, intlLocale: 'de-DE' },
  en: { label: 'English', dictionary: en, intlLocale: 'en-GB' },
};

const STORAGE_KEY = 'audit.locale';
const DEFAULT_LOCALE: LocaleCode = 'de';

export interface I18nContextValue {
  locale: LocaleCode;
  intlLocale: string;
  setLocale: (locale: LocaleCode) => void;
  /** Uebersetzt einen Schluessel; `{platzhalter}` werden ersetzt. */
  t: (key: TranslationKeys, params?: Record<string, string | number>) => string;
  /**
   * Zaehlbare Angabe in der richtigen Zahlform.
   *
   * „1 Einträge“ ist schlicht falsch, und ein Katalog mit genau einem
   * Eintrag ist kein Sonderfall, sondern der Normalfall am Anfang. Die
   * Entscheidung trifft `Intl.PluralRules`, nicht ein Vergleich mit 1 – eine
   * dritte Sprache bringt eigene Regeln mit. `{count}` ist bereits gesetzt.
   */
  plural: (one: TranslationKeys, other: TranslationKeys, count: number) => string;
  formatNumber: (value: number | null | undefined) => string;
  formatPercent: (value: number | null | undefined) => string;
  formatDateTime: (value: string | Date | null | undefined) => string;
  formatDate: (value: string | Date | null | undefined) => string;
  formatDuration: (milliseconds: number | null | undefined) => string;
  /** Betrag in Waehrung; ohne Waehrung als Zahl mit zwei Nachkommastellen. */
  formatCurrency: (value: number | null | undefined, currency?: string | null) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

/**
 * Waehlt zwischen Einzahl- und Mehrzahlform.
 *
 * Bewusst ueber `Intl.PluralRules` statt ueber `count === 1`: Deutsch und
 * Englisch kaemen mit dem Vergleich aus, eine dritte Sprache nicht — und die
 * Stelle, an der das auffiele, ist eine Beschriftung, die niemand prueft,
 * weil sie „nur Text" ist.
 *
 * Als eigene Funktion, damit sie ohne React und ohne DOM pruefbar ist.
 */
export function pluralForm<T>(intlLocale: string, one: T, other: T, count: number): T {
  return new Intl.PluralRules(intlLocale).select(count) === 'one' ? one : other;
}

function readStoredLocale(): LocaleCode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && stored in LOCALES) return stored as LocaleCode;
    const browser = window.navigator.language.slice(0, 2);
    if (browser in LOCALES) return browser as LocaleCode;
  } catch {
    // Zugriff auf localStorage kann blockiert sein; Standardsprache genuegt.
  }
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(readStoredLocale);

  const setLocale = useCallback((next: LocaleCode) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
      document.documentElement.lang = next;
    } catch {
      // Persistenz ist optional.
    }
  }, []);

  const value = useMemo<I18nContextValue>(() => {
    const { dictionary, intlLocale } = LOCALES[locale];
    const numberFormat = new Intl.NumberFormat(intlLocale);
    const percentFormat = new Intl.NumberFormat(intlLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const dateTimeFormat = new Intl.DateTimeFormat(intlLocale, { dateStyle: 'medium', timeStyle: 'short' });
    const dateFormat = new Intl.DateTimeFormat(intlLocale, { dateStyle: 'medium' });

    const toDate = (value: string | Date | null | undefined): Date | null => {
      if (!value) return null;
      const date = value instanceof Date ? value : new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    return {
      locale,
      intlLocale,
      setLocale,
      t: (key, params) => {
        const template = dictionary[key] ?? key;
        if (!params) return template;
        return template.replace(/\{(\w+)\}/g, (match, name: string) =>
          name in params ? String(params[name]) : match,
        );
      },
      plural: (one, other, count) => {
        const key = pluralForm(intlLocale, one, other, count);
        const template = dictionary[key] ?? key;
        return template.replace(/\{count\}/g, numberFormat.format(count));
      },
      formatNumber: (value) => (value === null || value === undefined ? '–' : numberFormat.format(value)),
      formatPercent: (value) => (value === null || value === undefined ? '–' : `${percentFormat.format(value)} %`),
      formatDateTime: (value) => {
        const date = toDate(value);
        return date ? dateTimeFormat.format(date) : '–';
      },
      formatDate: (value) => {
        const date = toDate(value);
        return date ? dateFormat.format(date) : '–';
      },
      formatCurrency: (value, currency) => {
        if (value === null || value === undefined) return '–';
        if (currency) {
          try {
            return new Intl.NumberFormat(intlLocale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(value);
          } catch {
            // Unbekannter Waehrungscode: Zahl plus Code statt Absturz.
            return `${value.toFixed(2)} ${currency}`;
          }
        }
        return new Intl.NumberFormat(intlLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
      },
      formatDuration: (milliseconds) => {
        if (milliseconds === null || milliseconds === undefined) return '–';
        const seconds = Math.round(milliseconds / 1000);
        if (seconds < 60) return `${seconds} s`;
        const minutes = Math.floor(seconds / 60);
        const rest = seconds % 60;
        if (minutes < 60) return `${minutes} min ${rest} s`;
        return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
      },
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n muss innerhalb von <I18nProvider> verwendet werden.');
  return ctx;
}

export type { TranslationKeys };
